/**
 * Stage 35 — Russian presentation dictionaries for the cross-unit surface.
 *
 * Presentation only. Backend keys are never changed and never lost: every lookup falls
 * back to the raw key, and each rendered item keeps its technical key available for a
 * tooltip / `data-*` attribute. Nothing here affects a score, a level or a count.
 *
 * The vocabularies are transcribed from the live catalog and the model schema, not
 * invented:
 *   • 46 Track A features  — models/track_a_cross_unit_broad/v0.2.0-weak-candidate/
 *                            feature_schema.json → feature_order
 *   • 8 anomaly reason codes — SELECT DISTINCT anomaly_reason_codes …
 *   • 4 unit types           — SELECT DISTINCT unit_type FROM shipment_leg
 */
import type { TranslateFn } from '@/lib/i18n'


/* ── Stage 38: the Track A SHAP feature dictionary was removed ───────────────
 *
 * `TRACK_A_FEATURE_RU` / `featureTitle` translated the 46 priority-model features for the
 * «Что повлияло на приоритет проверки» block. With that block gone, the only place SHAP
 * appears is «Технические сведения», which prints the backend feature keys verbatim — a
 * translated label there would hide the key a reviewer needs.
 */

/* ── anomaly reason codes (8, complete) ───────────────────────────────────── */

export function anomalyReasonLabels(
  t: TranslateFn,
): Record<string, { title: string; hint: string }> {
  return {
    UNUSUAL_TIMING: {
      title: t('analytics.cu.reason.unusualTiming.title'),
      hint: t('analytics.cu.reason.unusualTiming.hint'),
    },
    UNUSUAL_PARTICIPANT_HANDOFF: {
      title: t('analytics.cu.reason.unusualParticipantHandoff.title'),
      hint: t('analytics.cu.reason.unusualParticipantHandoff.hint'),
    },
    UNUSUAL_PAYER_RELATIONSHIP: {
      title: t('analytics.cu.reason.unusualPayerRelationship.title'),
      hint: t('analytics.cu.reason.unusualPayerRelationship.hint'),
    },
    UNUSUAL_CARGO_TRANSITION_HISTORY: {
      title: t('analytics.cu.reason.unusualCargoTransitionHistory.title'),
      hint: t('analytics.cu.reason.unusualCargoTransitionHistory.hint'),
    },
    UNUSUAL_JUNCTION_ACTIVITY: {
      title: t('analytics.cu.reason.unusualJunctionActivity.title'),
      hint: t('analytics.cu.reason.unusualJunctionActivity.hint'),
    },
    UNUSUAL_UNIT_ACTIVITY_DIVERGENCE: {
      title: t('analytics.cu.reason.unusualUnitActivityDivergence.title'),
      hint: t('analytics.cu.reason.unusualUnitActivityDivergence.hint'),
    },
    UNUSUAL_CORRIDOR: {
      title: t('analytics.cu.reason.unusualCorridor.title'),
      hint: t('analytics.cu.reason.unusualCorridor.hint'),
    },
    UNUSUAL_WEIGHT_RELATION: {
      title: t('analytics.cu.reason.unusualWeightRelation.title'),
      hint: t('analytics.cu.reason.unusualWeightRelation.hint'),
    },
  }
}

export interface RenderedReason {
  code: string
  title: string
  hint: string
  /** true when the code is absent from the dictionary and shown as-is. */
  untranslated: boolean
}

export function reasonTitle(code: string, t: TranslateFn): RenderedReason {
  const c = String(code || '').trim()
  const hit = anomalyReasonLabels(t)[c]
  return hit
    ? { code: c, title: hit.title, hint: hit.hint, untranslated: false }
    : { code: c, title: prettifyKey(c), hint: '', untranslated: true }
}

/* ── Stage 37: transport-unit and data-limitation dictionaries removed ───────
 *
 * `UNIT_TYPE_RU` / `unitTypeLabel` labelled the catalog's `unit_type` next to a leg's
 * wagon number. The analyst card states the unit itself («вагон 95263760 · контейнер …»),
 * so the type word had nothing left to add.
 *
 * `limitationText` translated the six corpus-level `LEG_DATA_LIMITATIONS` sentences
 * («вес указан примерно у 7% плеч» …) for the «Ограничения данных» block. Those are
 * properties of the whole catalog, not of the open case, and the block is gone: the raw
 * English sentences remain reachable verbatim in «Технические сведения».
 */

/* ── Russian plurals ──────────────────────────────────────────────────────── */

/**
 * Correct Russian plural selection: 1 кандидат / 3 кандидата / 5 кандидатов.
 * `forms` = [one, few, many].
 */
export function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(Math.trunc(n))
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1]
  return forms[2]
}

/* ── shared ───────────────────────────────────────────────────────────────── */

/** `snake_case_key` → `Snake case key`, used whenever a dictionary entry is missing. */
export function prettifyKey(name: string): string {
  const s = String(name || '').trim()
  if (!s) return '—'
  return s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}
